'use client';

/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the application.
 * Fully type-safe with proper error handling and token management.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, TokenManager, ApiError } from '@/lib/api';
import type { User } from '@/types/api';

// ============================================================================
// CONTEXT TYPES
// ============================================================================

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: ApiError | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  // ========================================
  // Load user from token
  // ========================================

  const loadUser = useCallback(async () => {
    try {
      const storedToken = TokenManager.getToken();
      if (!storedToken) {
        setLoading(false);
        return;
      }

      setToken(storedToken);

      // Fetch user profile
      const userData = await authAPI.getProfile();
      setUser(userData);
      setError(null);
    } catch (err) {
      console.error('Failed to load user:', err);

      // Only clear tokens for auth failures (401/403), not network errors
      if (err instanceof ApiError && (err.statusCode === 401 || err.statusCode === 403)) {
        TokenManager.clearTokens();
        setToken(null);
        setUser(null);
        setError(err);
      }
      // For network errors (server restarting, etc.), keep tokens and retry later
    } finally {
      setLoading(false);
    }
  }, []);

  // Load user on mount
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // ========================================
  // Login
  // ========================================

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const response = await authAPI.login({ email, password });
      const { accessToken, refreshToken, user: userData } = response;

      // Store tokens
      TokenManager.setToken(accessToken);
      if (refreshToken) {
        TokenManager.setRefreshToken(refreshToken);
      }

      // Update state
      setToken(accessToken);
      setUser(userData);
    } catch (err) {
      console.error('Login failed:', err);

      const apiError =
        err instanceof ApiError
          ? err
          : new ApiError(0, 'Falha ao fazer login', 'LOGIN_ERROR', err);

      setError(apiError);
      throw apiError;
    } finally {
      setLoading(false);
    }
  }, []);

  // ========================================
  // Login with Google
  // ========================================

  const loginWithGoogle = useCallback(async (credential: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const response = await authAPI.googleLogin(credential);
      const { accessToken, refreshToken, user: userData } = response;

      TokenManager.setToken(accessToken);
      if (refreshToken) {
        TokenManager.setRefreshToken(refreshToken);
      }

      setToken(accessToken);
      setUser(userData);
    } catch (err) {
      console.error('Google login failed:', err);

      const apiError =
        err instanceof ApiError
          ? err
          : new ApiError(0, 'Falha ao fazer login com Google', 'GOOGLE_LOGIN_ERROR', err);

      setError(apiError);
      throw apiError;
    } finally {
      setLoading(false);
    }
  }, []);

  // ========================================
  // Register
  // ========================================

  const register = useCallback(
    async (name: string, email: string, password: string): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        const response = await authAPI.register({ name, email, password });
        const { accessToken, refreshToken, user: userData } = response;

        // Store tokens
        TokenManager.setToken(accessToken);
        if (refreshToken) {
          TokenManager.setRefreshToken(refreshToken);
        }

        // Update state
        setToken(accessToken);
        setUser(userData);
      } catch (err) {
        console.error('Registration failed:', err);

        const apiError =
          err instanceof ApiError
            ? err
            : new ApiError(0, 'Falha ao criar conta', 'REGISTER_ERROR', err);

        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ========================================
  // Logout
  // ========================================

  const logout = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);

      // Call logout endpoint (optional - clears server-side session)
      await authAPI.logout().catch((err) => {
        console.warn('Logout endpoint failed:', err);
        // Continue with client-side logout even if server fails
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Always clear local state
      TokenManager.clearTokens();
      setToken(null);
      setUser(null);
      setError(null);
      setLoading(false);
    }
  }, []);

  // ========================================
  // Refresh user data
  // ========================================

  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const userData = await authAPI.getProfile();
      setUser(userData);
      setError(null);
    } catch (err) {
      console.error('Failed to refresh user:', err);

      const apiError =
        err instanceof ApiError
          ? err
          : new ApiError(0, 'Falha ao atualizar dados do usuário', 'REFRESH_ERROR', err);

      setError(apiError);
    } finally {
      setLoading(false);
    }
  }, []);

  // ========================================
  // Clear error
  // ========================================

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ========================================
  // Context value
  // ========================================

  const value: AuthContextType = {
    user,
    token,
    loading,
    error,
    login,
    loginWithGoogle,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user && !!token,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================================================
// HOOK
// ============================================================================

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

// ============================================================================
// PROTECTED ROUTE WRAPPER (Optional)
// ============================================================================

interface RequireAuthProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function RequireAuth({ children, fallback }: RequireAuthProps) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white">Carregando...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <>{fallback || null}</>;
  }

  return <>{children}</>;
}
